/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import { Chart as ChartJS } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	BarController,
	LineController,
} from 'chart.js';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';

import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import useTheme from '@mui/styles/useTheme';

import { useSessionState } from '../../../hooks/useSessionState';
import { DefaultFiltering } from '../../OrdersTable/components/OrderFilters';

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	BarController,
	LineController
);

function CustomChart({ data: dataProp, labels, className, ...rest }) {
	const theme = useTheme();
	const { t } = useTranslation('month');
	const filterChanges = useRef(0);

	const useStyles = makeStyles({
		root: {
			position: 'relative',
		},
	});

	const navigate = useNavigate();
	const classes = useStyles();

	function nFormatter(num, digits) {
		const si = [
			{ value: 1, symbol: '' },
			{ value: 1e3, symbol: 'k' },
			{ value: 1e6, symbol: 'M' },
			{ value: 1e9, symbol: 'G' },
			{ value: 1e12, symbol: 'T' },
			{ value: 1e15, symbol: 'P' },
			{ value: 1e18, symbol: 'E' },
		];
		const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
		let i;
		for (i = si.length - 1; i > 0; i -= 1) {
			if (num >= si[i].value) {
				break;
			}
		}
		return (num / si[i].value).toFixed(digits).replace(rx, '$1') + si[i].symbol;
	}

	const data = {
		datasets: [
			{
				label: 'Ordered',
				backgroundColor: theme.palette.navy.colorNavy70,
				hoverBackgroundColor: theme.palette.base.main,
				data: dataProp.ordered,
				barThickness: 30,
				maxBarThickness: 30,
				minBarLength: 2,
				barPercentage: 0.5,
				categoryPercentage: 0.5,
			},
		],
		labels,
	};

	const getMonthYearParts = (label) => {
		if (typeof label !== 'string') {
			return null;
		}

		const [month, year] = label.trim().split(/\s+/, 2);

		if (!month || !year) {
			return null;
		}

		return { month, year };
	};

	const handleBarClick = function (el, elements) {
		if (elements.length === 0) return;

		try {
			const bars = this.getElementsAtEventForMode(
				el,
				'nearest',
				{ intersect: true },
				true
			);
			const bar = bars[0];
			const { index } = bar;
			const { labels: chartLabels = [] } = this.data;
			const label = chartLabels[index];
			const monthYearParts = getMonthYearParts(label);

			if (!monthYearParts) {
				return;
			}

			const month = getMonthFromString(monthYearParts.month);
			const { year } = monthYearParts;
			const startDate = new Date(year, month, 1);
			const endDate = new Date(year, month + 1, 0);

			setFilters({
				orderDate: {
					startDate,
					endDate,
					timeframe: undefined,
					type: 'dateRange',
				},
			});
		} catch (err) {
			console.error(err);
		}
	};

	const handleBarHover = (event, chartElement) => {
		const { target } = event.native;
		if (chartElement.length === 1) {
			target.style.cursor = 'pointer';
		}
		if (chartElement.length === 0) {
			target.style.cursor = 'default';
		}
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		animation: false,
		cornerRadius: 20,
		layout: {
			padding: {
				top: 25,
			},
		},
		scales: {
			x: {
				grid: {
					display: false,
				},
				border: {
					display: false,
				},
				ticks: {
					display: true,
					padding: 10,
					color: theme.palette.text.secondary,
					callback(value) {
						const label = this.getLabelForValue(value);
						const monthYearParts = getMonthYearParts(label);

						if (!monthYearParts) {
							return label || '';
						}

						const tMonth = t(`${monthYearParts.month}`);
						const tYear = monthYearParts.year;
						return [tMonth, tYear];
					},
				},
			},
			y: {
				grid: {
					display: false,
				},
				border: {
					display: false,
				},
				ticks: {
					display: true,
					padding: 2,
					color: theme.palette.text.secondary,
					maxTicksLimit: 5,
					callback: (value) => (value > 0 ? `${nFormatter(value, 2)}` : value),
				},
			},
		},
		onClick: handleBarClick,
		onHover: handleBarHover,
		plugins: {
			tooltip: {
				enabled: true,
				mode: 'point',
				position: 'nearest',
				intersect: false,
				yAlign: 'bottom',
				xAlign: 'center',
				backgroundColor: 'transparent',
				bodyColor: theme.palette.text.secondary,
				displayColors: false,
				callbacks: {
					title: () => {},
					label: (tooltipItem) => {
						const label = `${nFormatter(tooltipItem.parsed.y, 2)}`;
						return label;
					},
					footerFontColor: 'red',
				},
			},
		},
	};

	function getMonthFromString(mon) {
		return new Date(Date.parse(`${mon} 1, 2020`)).getMonth();
	}

	const [filters, setFilters] = useSessionState(
		'orders_filters',
		DefaultFiltering
	);

	useEffect(() => {
		if (filterChanges.current) {
			navigate('/orders', { state: { isFilterOpen: true } });
		}

		filterChanges.current += 1;
	}, [filters, navigate]); // runs once on load. any other changes after that call for a redirect

	return (
		<div className={clsx(classes.root, className)} {...rest}>
			<Bar
				data={data}
				options={options}
				data-testid="chart_bar"
				data-testvalue={data}
			/>
		</div>
	);
}

CustomChart.propTypes = {
	className: PropTypes.string,
	data: PropTypes.object.isRequired,
	labels: PropTypes.array.isRequired,
};

export default CustomChart;
